import React,{useEffect, useMemo, useState} from "react";
import {Pair, ReservationSummaryWrapper, Row, RowTitle, RowValue, Subtitle, Title, Value} from "./styles";
import {useTranslation} from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";
import {Separator} from "../Separator";
import calculatorIcon from '../../../../assets/images/reservation/calculator.svg';
import creditCardIcon from '../../../../assets/images/reservation/credit_card.svg';
import popeyeIcon from '../../../../assets/images/reservation/popeye_head.svg';
import boatIcon from '../../../../assets/images/reservation/boat.svg';
import locationIcon from '../../../../assets/images/reservation/location.svg';
import {formatLocalizedDate} from "../../../../utils/dates";
import {formatPrice, formatPriceNoCurrency} from "../../../../utils/format";
import attachmentService from "services/attachment";
import ConfirmModal from "components/UI/ConfirmModal/ConfirmModal";
import Switch from "../../../../components/UI/Antd/Switch/Switch";


const ReservationSummary = props => {
    const {
        startDate,
        endDate,
        vesselLength,
        vesselBeam,
        vesselDraft,
        vesselType,
        hullType,
        natureOfTravel,
        marina,
        marinaData,
        proposal,
        long_term_reservation,
        usePopeyeBalance,
        setUsePopeyeBalance,
        totalToPay,
        disableChanges
    } = props;
    const {t} = useTranslation();
    const [cancellationPolicy, setCancellationPolicy] = useState(null);
    const [beforeConfirmOpened, setBeforeConfirmOpened] = useState(false);

    useEffect(() => {
        const fetchAttachment = async () => {
            if(marina){
                try {
                    const requested_type = long_term_reservation ? 'CANCELLATION_POLICY_LONG' : 'CANCELLATION_POLICY_SHORT';
                    const attachment = await attachmentService.getAttachmentByType({ requested_type, marina_id:marina.id, hello:'wtf' });
                    setCancellationPolicy(attachment && attachment[0]);
                } catch (error) {
                    console.error('Error fetching attachment:', error);
                }
            }
        };

        fetchAttachment();
    }, [long_term_reservation, marina]);

    const marinaLogos = useMemo(() =>
        ( (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina?.id === 411) ||
            (process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina?.id === 82) )

    , [marina])

    const marinaPuertoSherry = useMemo(() =>
        ( (process.env.REACT_APP_FIREBASE_ENV === 'TEST' && marina?.id === 352) ||
            (process.env.REACT_APP_FIREBASE_ENV === 'PRODUCTION' && marina?.id === 98) ||
            process.env.REACT_APP_FIREBASE_ENV === 'DEVELOPMENT' && marina?.id ===7 )

    , [marina]);

    const depositDesc = useMemo(() => {
        if(proposal?.price?.deposit_rule) {
            switch(proposal.price.deposit_rule.deposit_type) {
                case 'PERCENTAGE': {
                    return t('PAY_NOW_ONLY_DEPOSIT', {amount: `${parseFloat(proposal.price.deposit_rule.amount).toLocaleString('en', {
                            maximumFractionDigits: 2
                        })}%`})
                }
                case 'FIXED_AMOUNT': {
                    return t('DEPOSIT_TO_PAY_NOW')
                }
                case 'NIGHTS': {
                    const depositNightsCount = parseFloat(proposal.price.deposit_rule.amount);
                    const actualDuration = proposal?.price?.reservation_duration ?? 1;
                    const finalCount = Math.min(depositNightsCount, actualDuration);
                    return t(finalCount > 1 ? 'PAY_NOW_ONLY_NIGHTS_DEPOSIT' : 'PAY_NOW_ONLY_NIGHT_DEPOSIT',
                        {amount: `${finalCount.toLocaleString('en', { maximumFractionDigits: 0 })}`})

                }
            }
        }
        return null;
    }, [proposal?.price, marina]);

    return (<ReservationSummaryWrapper>
        <Title>{t('SUMMARY')}</Title>
        <Subtitle>{t('WHERE')}</Subtitle>
        <Value>{marina?.name},&nbsp;{marinaData?.city},&nbsp;{marinaData?.country}</Value>
        <Separator icon={calculatorIcon}/>
        <Subtitle>{t('WHEN')}</Subtitle>
        <Row>
            <Pair>
                <RowTitle>{t('CHECK_IN')}</RowTitle>
                <RowValue bold>{startDate ? formatLocalizedDate(moment(startDate)) : '-'}</RowValue>
            </Pair>
        </Row>
        <Row>
            <Pair>
                <RowTitle>{t('CHECK_OUT')}</RowTitle>
                <RowValue bold>{endDate ? formatLocalizedDate(moment(endDate)) : '-'}</RowValue>
            </Pair>
            <RowValue bold>{t(proposal?.price?.reservation_duration === 1 ?
                'TOTAL_NIGHT' : 'TOTAL_NIGHTS', {nights: proposal?.price?.reservation_duration})}
            </RowValue>
        </Row>
        <Separator icon={boatIcon}/>
        <Subtitle>{t('BOAT')}</Subtitle>
        {(vesselLength || vesselBeam || vesselDraft) && <Pair>
            <RowTitle>{t('BOAT_SIZE')}</RowTitle>
            <RowValue>{t('BOAT_SIZE_VALUE', {
                loa: vesselLength || '-',
                beam: vesselBeam || '-',
                draft: vesselDraft || '-',
            })}</RowValue>
        </Pair>}
        {vesselType && <Pair>
            <RowTitle>{t('BOAT_TYPE')}</RowTitle>
            <RowValue>{t(vesselType)}</RowValue>
        </Pair>}
        {hullType && <Pair>
            <RowTitle>{t('HULL_TYPE')}</RowTitle>
            <RowValue>{t(hullType)}</RowValue>
        </Pair>}
        {natureOfTravel && <Pair>
            <RowTitle>{t('TRAVEL_ESSENCE')}</RowTitle>
            <RowValue>{t(natureOfTravel)}</RowValue>
        </Pair>}
        <Separator icon={locationIcon}/>
        <Subtitle>{t('BOOKING_DETAILS')}</Subtitle>
        <Row>
            <RowTitle>{t(proposal?.price?.reservation_duration === 1 ? 'BERTHING_NIGHT' : 'BERTHING_NIGHTS',
                {nights: proposal?.price?.reservation_duration})}</RowTitle>
            <RowValue green>
                {formatPriceNoCurrency(proposal?.price?.total_before_discounts?.toString(), undefined)}
            </RowValue>
        </Row>
        {(proposal?.price?.total_discounts || 0) > 0 && <Row>
            <RowTitle>{t('MARINA_DISCOUNT', {percentage: `${proposal?.price?.discount_percent}%`})}</RowTitle>
            <RowValue green>{formatPriceNoCurrency(proposal?.price?.total_discounts?.toString())}</RowValue>
        </Row>}
        {(proposal?.price?.total_custom_surcharges || 0) > 0 && <Row>
            <RowTitle>{t('CUSTOM_SUB_CHARGES')}</RowTitle>
            <RowValue green>{formatPriceNoCurrency(proposal?.price?.total_custom_surcharges?.toString())}</RowValue>
        </Row>}
        {marinaLogos && (
        <Row>
          <RowTitle>
            {t("WATER_AND_ELECTRICITY", {
              currency: marina?.marina_settings.currency,
            })}
          </RowTitle>
          <RowValue green>
            {t("INCLUDED")}
          </RowValue>
        </Row>)}
        {marinaPuertoSherry && (
        <Row>
          <RowTitle>
            {t("WATER_AND_ELECTRICITY", {
              currency: marina?.marina_settings.currency,
            })}
          </RowTitle>
          <RowValue green>
            {(vesselLength > 20 )? t("NOT_INCLUDED") : t("INCLUDED")}
          </RowValue>
        </Row>)}
        <Row>
            <RowTitle bold>
                {t('TOTAL_INCLUDING_TAX', {currency: marina?.marina_settings.currency})}
            </RowTitle>
            <RowValue green bold>{formatPriceNoCurrency(totalToPay?.toString())}</RowValue>
        </Row>
        <Row>
            <RowTitle>
                <i>{t('PRICE_IS_NOT_FINAL')}</i>
            </RowTitle>
        </Row>
        { proposal?.price?.deposit && proposal.price.total_to_pay !== proposal.price.deposit ? <Separator icon={creditCardIcon} /> : '' }
        { proposal?.price?.deposit && proposal.price.total_to_pay !== proposal.price.deposit ? <Row>
            <RowTitle bold>
                { depositDesc }
            </RowTitle>
            <RowValue green bold>{formatPriceNoCurrency(proposal?.price?.deposit?.toString())}</RowValue>
        </Row> : ''}

        { proposal?.price?.popeye_discount?.total_discount_via_points &&
            proposal?.price?.popeye_discount?.total_points_to_redeem && <>
                <Separator icon={popeyeIcon} />
                <Subtitle>Popeye® Sail Club</Subtitle>
                { !disableChanges && <Row>
                    <RowTitle bold>
                        {t('POPEYE_RESERVATION_CHECKOUT_USE_POINTS')}
                    </RowTitle>
                    <RowValue>
                        <Switch
                            checked={usePopeyeBalance}
                            onChange={checked =>
                                setUsePopeyeBalance(checked)
                            }
                            size='small'
                        />
                    </RowValue>
                </Row> }
                <Row>
                    <RowTitle bold>
                        {t('RESERVATION_PRICE_BREAKDOWN_POINTS')}
                    </RowTitle>
                    <RowValue red>
                        - {formatPrice(proposal?.price?.popeye_discount.total_discount_via_points,
                        'en', marina?.marina_settings.currency )}
                    </RowValue>
                </Row>
            </> }
        <>
                 <br></br>
        <Row>
            <RowTitle>
                <i>
                    {t('CANCELATION_OPTION')}&nbsp;
                    <span className="cancellation_policy" onClick={() => {
                        if(cancellationPolicy && cancellationPolicy.url){
                            window.open(cancellationPolicy?.url);
                        }else{
                            setBeforeConfirmOpened(true);
                        }

                       }}>{t('CANCELLATION_POLICY')}</span>
                </i>
            </RowTitle>
        </Row>
            </>
            <ConfirmModal
                title={"Cancellation policy not available "}
                visible={beforeConfirmOpened}
                confirmButtonTitle={ t('OK') }
                onConfirm={() => {setBeforeConfirmOpened(false); }}
                asyncConfirm={true}
                onCancel={() => setBeforeConfirmOpened(false)}
                hideBackBtn = { true }
            >

                {t('NO_CANCELLATION_POLICY_MSG',{ marina_phone: marina?.country_code + marina?.phone} )}
            </ConfirmModal>
    </ReservationSummaryWrapper>)
}

ReservationSummary.propTypes = {
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    vesselLength: PropTypes.number,
    vesselBeam: PropTypes.number,
    vesselDraft: PropTypes.number,
    vesselType: PropTypes.string,
    hullType: PropTypes.string,
    natureOfTravel: PropTypes.string,
    marina: PropTypes.object,
    marinaData: PropTypes.object,
    proposal: PropTypes.object,
    totalToPay: PropTypes.number,
    long_term_reservation: PropTypes.bool
}

export default ReservationSummary;
